"use client";

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import {
  Coins,
  Plus,
  RefreshCw,
  Sparkles,
  Search,
  TrendingUp,
  Trash2,
  Pencil,
  X,
} from "lucide-react";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgToast } from "@/components/fg-toast";
import { EmptyState } from "@/components/empty-state";
import { api } from "@/lib/api";

type Tab = "packs" | "balances" | "analytics";

interface Pack {
  id: string;
  name: string;
  credits: number;
  priceCents: number;
  currency: string;
  validityDays: number | null;
  isActive: boolean;
  orderIndex: number;
}

interface LedgerEntry {
  id: string;
  userId: string;
  delta: number;
  reason:
    | "subscription_grant"
    | "purchase"
    | "consume"
    | "refund"
    | "admin_grant"
    | "expiry";
  levelCode: string | null;
  activity: string | null;
  balanceAfter: number;
  expiresAt: string | null;
  createdAt: string;
}

const REASON_TONE: Record<LedgerEntry["reason"], string> = {
  subscription_grant: "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30",
  purchase: "bg-[#0A84FF]/15 text-[#0A84FF] border-[#0A84FF]/30",
  consume: "bg-[#FF453A]/15 text-[#FF453A] border-[#FF453A]/30",
  refund: "bg-[#FFD60A]/15 text-[#FFD60A] border-[#FFD60A]/30",
  admin_grant: "bg-[#AF52DE]/15 text-[#AF52DE] border-[#AF52DE]/30",
  expiry: "bg-[#8E8E93]/15 text-[#8E8E93] border-[#8E8E93]/30",
};

export function CreditsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("packs");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Pack | null>(null);
  const [deleting, setDeleting] = useState<Pack | null>(null);

  const packs = useQuery<Pack[]>({
    queryKey: ["admin", "credit-packs"],
    queryFn: async () => (await api.get("/api/credits/packs")).data,
  });

  const seedPacks = useMutation({
    mutationFn: async () => (await api.post("/api/credits/admin/packs/seed")).data,
    onSuccess: (data: { inserted: number; skipped: number }) => {
      qc.invalidateQueries({ queryKey: ["admin", "credit-packs"] });
      FgToast.success(`Seeded ${data.inserted} new packs (${data.skipped} already existed).`);
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Seed failed"),
  });

  const deletePack = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/credits/admin/packs/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "credit-packs"] });
      FgToast.success("Pack deleted");
      setDeleting(null);
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Delete failed"),
  });

  return (
    <AdminShell>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[rgba(255,255,255,0.08)] pb-6 mb-8"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Coins className="w-5 h-5 text-[#FFD60A]" />
            <span className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#FFD60A]">
              AI CREDITS
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#FAFAFA]">
            Credits &amp; Packs
          </h1>
          <p className="mt-1 text-sm text-[#8E8E93]">
            Pay-as-you-go packs · ledger · manual grants · analytics
          </p>
        </div>
        <FgButton
          size="sm"
          variant="primary"
          onClick={() => seedPacks.mutate()}
          loading={seedPacks.isPending}
          leftIcon={<Sparkles className="w-4 h-4" />}
        >
          Seed default packs
        </FgButton>
      </motion.div>

      <div className="mb-6 inline-flex items-center gap-1 p-1 rounded-xl bg-[#1C1C1E]/60 border border-[rgba(255,255,255,0.08)] backdrop-blur">
        {(
          [
            { id: "packs" as const, label: "Packs" },
            { id: "balances" as const, label: "User Balances" },
            { id: "analytics" as const, label: "Analytics" },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              "relative px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors",
              tab === t.id ? "text-[#0A0A0A]" : "text-[#8E8E93] hover:text-[#FAFAFA]",
            )}
          >
            {tab === t.id && (
              <motion.div
                layoutId="credits-tab"
                className="absolute inset-0 bg-[#FFD60A] rounded-lg"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            <span className="relative z-10">{t.label}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {tab === "packs" && (
            <PacksPanel
              packs={packs.data ?? []}
              loading={packs.isLoading}
              onRefresh={() => packs.refetch()}
              onCreate={() => setCreating(true)}
              onEdit={(p) => setEditing(p)}
              onDelete={(p) => setDeleting(p)}
            />
          )}
          {tab === "balances" && <BalancesPanel />}
          {tab === "analytics" && <AnalyticsPanel />}
        </motion.div>
      </AnimatePresence>

      {(creating || editing) && (
        <PackEditor
          pack={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin", "credit-packs"] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] p-6 shadow-2xl">
            <h2 className="text-lg font-bold text-[#FAFAFA] mb-2">Delete pack?</h2>
            <p className="text-sm text-[#8E8E93] mb-6">
              &quot;{deleting.name}&quot; will be removed permanently.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleting(null)}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-[#8E8E93] hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                onClick={() => deletePack.mutate(deleting.id)}
                disabled={deletePack.isPending}
                className="rounded-lg bg-[#FF453A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#FF453A]/90 disabled:opacity-50"
              >
                {deletePack.isPending ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

function PacksPanel({
  packs,
  loading,
  onRefresh,
  onCreate,
  onEdit,
  onDelete,
}: {
  packs: Pack[];
  loading: boolean;
  onRefresh: () => void;
  onCreate: () => void;
  onEdit: (p: Pack) => void;
  onDelete: (p: Pack) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-[#8E8E93]">
          One-time purchases · non-expiring (soft 90-day nudge).
        </p>
        <div className="flex items-center gap-2">
          <FgButton
            size="sm"
            variant="secondary"
            onClick={onRefresh}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Refresh
          </FgButton>
          <FgButton
            size="sm"
            variant="primary"
            onClick={onCreate}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            New pack
          </FgButton>
        </div>
      </div>

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 rounded-2xl bg-white/5 fg-skeleton" />
          ))}
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {packs.map((p, idx) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
            >
              <FgCard className="p-5 h-full flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-bold text-[#FAFAFA]">{p.name}</h3>
                    {!p.isActive && (
                      <span className="font-mono text-[10px] bg-[#8E8E93]/15 text-[#8E8E93] border border-[#8E8E93]/30 px-1.5 py-0.5 rounded uppercase">
                        inactive
                      </span>
                    )}
                  </div>
                  <p className="text-3xl font-extrabold text-[#FFD60A] counter-text">
                    {p.credits.toLocaleString()}
                  </p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#8E8E93]">
                    credits
                  </p>
                </div>
                <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.08)]">
                  <p className="text-lg font-bold text-[#FAFAFA]">
                    ${(p.priceCents / 100).toFixed(2)}{" "}
                    <span className="text-[10px] text-[#8E8E93] font-mono uppercase">
                      {p.currency}
                    </span>
                  </p>
                  <p className="text-[10px] text-[#8E8E93] font-mono mt-1">
                    ${(p.priceCents / p.credits / 100).toFixed(3)} / credit
                  </p>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <FgButton
                      size="sm"
                      variant="ghost"
                      onClick={() => onEdit(p)}
                      leftIcon={<Pencil size={12} />}
                    >
                      Edit
                    </FgButton>
                    <FgButton
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(p)}
                      leftIcon={<Trash2 size={14} />}
                    >
                      Delete
                    </FgButton>
                  </div>
                </div>
              </FgCard>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function BalancesPanel() {
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserId] = useState<string | null>(null);
  const history = useQuery<LedgerEntry[]>({
    queryKey: ["admin", "credit-history", selectedUserId],
    queryFn: async () =>
      (await api.get(`/api/credits/history?userId=${selectedUserId}&limit=50`)).data,
    enabled: !!selectedUserId,
  });

  return (
    <div className="space-y-4">
      <FgCard className="p-5">
        <h2 className="text-sm font-bold text-[#FAFAFA] mb-3">Find a user</h2>
        <div className="relative">
          <Search
            size={13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E8E93]"
          />
          <input
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Search by email or name… (uses /api/admin/users)"
            className="w-full h-9 rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] pl-7 pr-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
          />
        </div>
        <p className="mt-2 text-[10px] text-[#8E8E93]">
          Pick a user from the search dropdown to see their ledger history. (User
          picker wires to /api/admin/users in a follow-up patch.)
        </p>
      </FgCard>

      {selectedUserId && (
        <FgCard className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#8E8E93]">
              {history.isLoading ? "Loading…" : `${history.data?.length ?? 0} entries`}
            </p>
            <FgButton
              size="sm"
              variant="primary"
              leftIcon={<Plus className="w-3.5 h-3.5" />}
              onClick={() => {
                const creditsStr = window.prompt("How many credits to grant?");
                const credits = creditsStr ? Number(creditsStr) : NaN;
                if (!Number.isFinite(credits) || credits <= 0) {
                  FgToast.error("Invalid amount");
                  return;
                }
                api
                  .post("/api/credits/admin/grant", {
                    userId: selectedUserId,
                    credits,
                    note: "Manual admin grant",
                  })
                  .then(() => {
                    FgToast.success(`Granted ${credits} credits`);
                    history.refetch();
                  })
                  .catch((e) =>
                    FgToast.error((e as { message?: string }).message ?? "Grant failed"),
                  );
              }}
            >
              Grant credits
            </FgButton>
          </div>
          <div className="space-y-1.5">
            {!history.isLoading && (history.data ?? []).length === 0 ? (
              <EmptyState
                tone="muted"
                icon={<Coins className="w-7 h-7" />}
                title="No credit activity yet"
                description="This user hasn't earned or spent any Learn Credits. Once they subscribe, you'll see grants here."
              />
            ) : null}
            {(history.data ?? []).map((e) => (
              <div
                key={e.id}
                className="flex items-center gap-3 rounded-md bg-white/[0.02] border border-[rgba(255,255,255,0.04)] px-2.5 py-1.5 text-xs"
              >
                <span
                  className={clsx(
                    "inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider",
                    REASON_TONE[e.reason],
                  )}
                >
                  {e.reason}
                </span>
                <span
                  className={clsx(
                    "font-mono font-bold w-12 text-right",
                    e.delta > 0 ? "text-[#34C759]" : "text-[#FF453A]",
                  )}
                >
                  {e.delta > 0 ? "+" : ""}
                  {e.delta}
                </span>
                <span className="font-mono text-[10px] text-[#8E8E93] flex-1 truncate">
                  {e.activity ?? e.levelCode ?? "—"}
                </span>
                <span className="font-mono text-[10px] text-[#8E8E93]">
                  bal {e.balanceAfter}
                </span>
                <span className="font-mono text-[10px] text-[#8E8E93]">
                  {new Date(e.createdAt).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </FgCard>
      )}
    </div>
  );
}

function AnalyticsPanel() {
  return (
    <FgCard className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-[#FFD60A]" />
        <h2 className="text-sm font-bold text-[#FAFAFA]">Learn Credit analytics</h2>
      </div>
      <p className="text-xs text-[#8E8E93]">
        Phase F ships the full cost dashboard: total credits issued, total
        consumed, top 10 costliest users, kill-switch toggle, and a 30-day
        cost-over-time chart.
      </p>
      <p className="mt-3 text-xs text-[#FFD60A] flex items-center gap-1.5">
        Coming soon — for now, query <code>credit_ledger</code> + <code>ai_cost_logs</code> directly.
      </p>
    </FgCard>
  );
}

function PackEditor({
  pack,
  onClose,
  onSaved,
}: {
  pack: Pack | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(pack?.name ?? "");
  const [credits, setCredits] = useState(String(pack?.credits ?? 10));
  const [priceCents, setPriceCents] = useState(String(pack?.priceCents ?? 99));
  const [currency, setCurrency] = useState(pack?.currency ?? "USD");
  const [validityDays, setValidityDays] = useState(
    pack?.validityDays == null ? "" : String(pack.validityDays),
  );
  const [orderIndex, setOrderIndex] = useState(String(pack?.orderIndex ?? 100));
  const [isActive, setIsActive] = useState(pack?.isActive ?? true);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        credits: Number(credits) || 0,
        priceCents: Number(priceCents) || 0,
        currency,
        validityDays: validityDays ? Number(validityDays) : null,
        orderIndex: Number(orderIndex) || 100,
        isActive,
      };
      return pack
        ? (await api.patch(`/api/credits/admin/packs/${pack.id}`, body)).data
        : (await api.post("/api/credits/admin/packs", body)).data;
    },
    onSuccess: () => {
      FgToast.success(pack ? "Pack updated" : "Pack created");
      onSaved();
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Save failed"),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-12">
      <div className="w-full max-w-lg rounded-2xl border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-[#FAFAFA]">
            {pack ? `Editing ${pack.name}` : "New pack"}
          </h2>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-[#8E8E93] hover:bg-white/10 hover:text-[#FAFAFA]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[#8E8E93]">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#2C2C2E] px-3 py-2 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:border-[#FFD60A] focus:outline-none"
              placeholder="e.g. Starter Pack"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#8E8E93]">Credits</label>
              <input
                type="number"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                className="w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#2C2C2E] px-3 py-2 text-sm text-[#FAFAFA] focus:border-[#FFD60A] focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#8E8E93]">Price (cents)</label>
              <input
                type="number"
                value={priceCents}
                onChange={(e) => setPriceCents(e.target.value)}
                className="w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#2C2C2E] px-3 py-2 text-sm text-[#FAFAFA] focus:border-[#FFD60A] focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#8E8E93]">Currency</label>
              <input
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                className="w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#2C2C2E] px-3 py-2 text-sm text-[#FAFAFA] focus:border-[#FFD60A] focus:outline-none"
                placeholder="USD"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#8E8E93]">Validity days (null = never)</label>
              <input
                type="number"
                value={validityDays}
                onChange={(e) => setValidityDays(e.target.value)}
                className="w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#2C2C2E] px-3 py-2 text-sm text-[#FAFAFA] focus:border-[#FFD60A] focus:outline-none"
                placeholder="90"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#8E8E93]">Order index</label>
              <input
                type="number"
                value={orderIndex}
                onChange={(e) => setOrderIndex(e.target.value)}
                className="w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#2C2C2E] px-3 py-2 text-sm text-[#FAFAFA] focus:border-[#FFD60A] focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-[rgba(255,255,255,0.2)] bg-[#2C2C2E] accent-[#FFD60A]"
              />
              <label htmlFor="isActive" className="text-sm text-[#FAFAFA]">Active</label>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-[#8E8E93] hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !name.trim()}
            className="rounded-lg bg-[#FFD60A] px-4 py-2 text-sm font-bold text-[#0A0A0A] hover:bg-[#FFD60A]/90 disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : pack ? "Update pack" : "Create pack"}
          </button>
        </div>
      </div>
    </div>
  );
}
